// Environment pills (slice-2-onboarding.md §8.2 "Environment pills - toggle vs.
// locked"). A required, non-toggleable environment (the fingerprint floor) renders as a
// STATIC labeled state - no click target, no button semantics at all (a disabled button
// would wrongly imply "could be enabled") - `role="status"`, the reason baked into the
// accessible name (§8.5). Every other environment is a real toggle
// (component tokens: toggle.trackWidth/trackBg/trackBgOn/knobBg, brand.css:139-142).
import type { EnvironmentRecommendation, EnvironmentType } from "@nightcrew/shared";

const ENV_LABEL: Record<EnvironmentType, string> = {
  local: "local",
  dev: "dev",
  staging: "staging",
  preproduction: "pre-production",
  production: "production",
};

const LockIcon = (): React.JSX.Element => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <rect x="4" y="11" width="16" height="9" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const EnvironmentPill = ({
  env,
  floorReason,
  onToggle,
}: {
  readonly env: EnvironmentRecommendation;
  readonly floorReason: string | null;
  readonly onToggle: (type: EnvironmentType, nextEnabled: boolean) => void;
}): React.JSX.Element => {
  const label = ENV_LABEL[env.type];

  if (env.required || !env.toggleable) {
    const reasonSuffix = floorReason !== null ? `, locked on because ${floorReason}` : "";
    return (
      <span className="env-pill req" role="status" aria-label={`${label}, required${reasonSuffix}`}>
        <LockIcon />
        {label} · required
      </span>
    );
  }

  return (
    <button
      type="button"
      className="env-toggle env-pill"
      role="switch"
      aria-checked={env.enabled}
      onClick={() => {
        onToggle(env.type, !env.enabled);
      }}
    >
      <span className={`toggle${env.enabled ? " on" : ""}`} aria-hidden="true" />
      {label}
    </button>
  );
};

export const EnvironmentPills = ({
  environments,
  floorReason,
  onToggle,
}: {
  readonly environments: readonly EnvironmentRecommendation[];
  readonly floorReason: string | null;
  readonly onToggle: (type: EnvironmentType, nextEnabled: boolean) => void;
}): React.JSX.Element => (
  <div className="row gap8" style={{ flexWrap: "wrap" }}>
    {environments.map((env) => (
      <EnvironmentPill key={env.type} env={env} floorReason={floorReason} onToggle={onToggle} />
    ))}
  </div>
);
